import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';

import { Navigate, useLocation } from 'react-router-dom';
import { BUILD_TIME, BUILD_VERSION } from '../../Settings';
import { TranscriptsButton } from '../Transcripts/Transcripts';
import { useDiscord } from '../../discord/useDiscord';
import { OWNER_MARK_GLYPH, OWNER_MARK_INGAME_LABEL } from './identityMarks';
import './escape-menu.css';

const VARIANTS = [
    null, 'deep', 'stem', 'hex', 'dread', 'honored', 'seed'
];

// CC_LOBBY_OVERLAY_WINDOW (2026-06-07): the in-game menu entry that
// summons the Lobby overlay window. Everyone (not owner-gated) can open
// it; it is in-game only. The entry fires a window event; the Desktop
// dashboard (DesktopRender) owns the toggle + the FloatingWindow. EscapeMenu
// is shared with the phone-companion layout, so the entry appears there
// too but the WINDOW is desktop-scoped (phone gets no window in v1; phone
// layout is Michael-driven). COPY PLACEHOLDER label — Michael owns wording.
const LOBBY_MENU_LABEL = 'Lobby';
const TOGGLE_LOBBY_WINDOW_EVENT = 'toggle-lobby-window-event';

export function EscapeMenu(props) {
    // const [ backState, setBackState ] = useState(false);
    //
    // if (backState) {
    //     setTimeout(props.app.game.returnToMenu, 0);
    //     setBackState(false);
    //     // // setTimeout(props.app.game.leaveGroup, 0);
    //     // if (props.location.pathname !== '/') return <Navigate to={'/'}/>;
    // }

    // CC_LOBBY_GAME_CRUD (2026-05-27): in-game Leave Game + Abandon
    // (Leave Game relabeled from Save & Exit by CC_GAME_NAMING) mirror
    // of the lobby-controls buttons. Owner-only; only surfaces
    // on /game/* paths (the in-game chrome). Owner check goes through
    // groupHandler.isGroupLeader() — defense-in-depth alongside the
    // server-side guards on save_game / delete_game.
    const [confirmingAbandon, setConfirmingAbandon] = useState(false);

    let variant = 'deep';
    let gamePlayerData = props.gamePlayerData;
    if (gamePlayerData) {
        variant = VARIANTS[gamePlayerData.faction];
    }

    let showTranscriptsButton = Boolean(props.app.gameController &&
        props.app.gameController.transcripts);

    // CC_MAIN_MENU shipped EscapeMenu under the new MainMenuShell ->
    // BottomMenu path, which doesn't thread `location` as a prop.
    // Source location from useLocation() so the Save/Abandon gate and
    // the Menu-back gate work regardless of how this is mounted.
    const location = useLocation();
    const pathname = (location && location.pathname)
        || (props.location && props.location.pathname) || '';
    const inGame = pathname.startsWith('/game/');
    const game = props.app && props.app.game;
    const groupHandler = game && game.groupHandler;
    const isOwner = Boolean(
        groupHandler && typeof groupHandler.isGroupLeader === 'function'
        && groupHandler.isGroupLeader());
    const currentGameId = (
        game && game.gameData && game.gameData.id) || null;
    const showSaveAbandon = inGame && isOwner;

    // CC_LIVING_GAMES_LIST_DESIGN: the public dial is a LIVE dial. The
    // owner can flip a game public/private DURING play (decided behavior
    // #1: "lost a player? go public"). The current value rides the Members
    // broadcast into groupHandler.lobbyConfig.public; the flip rides the
    // same Group/Configure rail the lobby GameConfig toggle uses, but with
    // `public` as a TOP-LEVEL options key (configureGroupOptions →
    // data.options.public), which is what the backend configure_group
    // reads. Owner-only, in-game only.
    const lobbyConfig = groupHandler && groupHandler.lobbyConfig;
    const isPublic = Boolean(lobbyConfig && lobbyConfig.public);
    const showPublicToggle = showSaveAbandon;
    const handleTogglePublic = () => {
        if (!game || !game.gameApi) return;
        game.gameApi.configureGroupOptions({ public: !isPublic });
    };

    // CC_LOGOUT_ESCAPE_MENU (2026-06-05): Logout surfaces on open web
    // only. Inside the Discord Activity the session comes from the
    // Discord OAuth flow, so a logout would just wedge the embed.
    const discord = useDiscord();
    const showLogout = !discord.isEmbedded;

    const handleSaveExit = () => {
        if (!game || !game.gameApi) return;
        game.gameApi.saveGame();
        // CC_FE_PAGES (2026-06-01): leaving the game lands on the Main
        // Menu; the old '/casual' route is gone.
        window.dispatchEvent(
            new CustomEvent('quick-router-event',
                { detail: '/' }));
    };

    const handleAbandon = () => {
        if (!confirmingAbandon) {
            setConfirmingAbandon(true);
            return;
        }
        if (!game || !game.gameApi) return;
        if (!currentGameId) return;
        game.gameApi.abandonGame(currentGameId);
        setConfirmingAbandon(false);
        // CC_LOBBY_SAVE_FINISH (2026-06-03), F14: zero the owner's
        // in-memory groupHandler state (groupMembers / groupSeats /
        // memberRecord / _ownerUserId) before navigating, so a fast New
        // Game can't flash the abandoned lobby's stale seat layout. The
        // BE half-one fix already releases the owner's connection; this is
        // the FE in-memory residue.
        if (groupHandler
                && typeof groupHandler.leaveGameGroup === 'function') {
            groupHandler.leaveGameGroup();
        }
        // CC_FE_PAGES (2026-06-01): abandoning the game lands on the Main
        // Menu; the old '/casual' route is gone.
        window.dispatchEvent(
            new CustomEvent('quick-router-event',
                { detail: '/' }));
    };

    const handleLogout = () => {
        const logoutResult = props.app.forceLogoutButton();
        // Reload only after the logout POST settles — reloading immediately
        // can cancel the in-flight fetch and leave the session cookie alive.
        Promise.resolve(logoutResult).finally(() => {
            window.location.reload();
        });
    };

    return (<div id={'escape-menu-wrapper'} className={'escape-menu-wrapper'}
                 data-ftue-target={'escape-menu'}>
            <Dropdown>
                <Dropdown.Toggle
                    variant={variant}
                    id="dropdown-basic"
                    className={'escape-menu-button'}
                >
                    &#9776;
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    {isOwner &&
                        <div className={'escape-menu-owner-badge'}
                             title={OWNER_MARK_INGAME_LABEL}
                             aria-label={OWNER_MARK_INGAME_LABEL}>
                            {/* CC_SEAT_IDENTITY_MARKS: in-game owner mark
                              * — same asset as the lobby owner seat row.
                              * `isOwner` is read live from
                              * groupHandler.isGroupLeader() each render
                              * (not frozen at mount). PLACEHOLDER glyph +
                              * copy, flagged for Michael. */}
                            <span className={'escape-menu-owner-mark'}>
                                {OWNER_MARK_GLYPH}
                            </span>
                            {' '}{OWNER_MARK_INGAME_LABEL}
                        </div>
                    }
                    <Dropdown.Item>
                        <Button style={{ width: '100%' }}
                                variant={'stem'}
                                onClick={() => {
                                    props.app.configurationMenu.show();
                                }}>Config</Button>
                    </Dropdown.Item>
                    <Dropdown.Item
                        href={'https://forum.hauntedspacehotel.com/c/general/5'}
                        target={'_blank'}
                    >

                        <Button style={{ width: '100%' }}
                                variant={'hex'}
                        >
                            Help / Feedback
                        </Button>
                    </Dropdown.Item>
                    {showTranscriptsButton &&
                        <Dropdown.Item>
                            <TranscriptsButton {...props} />
                        </Dropdown.Item>
                    }
                    {inGame &&
                        <Dropdown.Item>
                            {/* CC_LOBBY_OVERLAY_WINDOW: summon the living
                              * Lobby overlay. Everyone, in-game only. Fires
                              * the toggle event the Desktop dashboard
                              * listens for; clicking again closes it. */}
                            <Button style={{ width: '100%' }}
                                    variant={'stem'}
                                    onClick={() => window.dispatchEvent(
                                        new CustomEvent(
                                            TOGGLE_LOBBY_WINDOW_EVENT))}
                                    title={'Show who is in the lobby and the '
                                        + 'game settings. Leave it up or '
                                        + 'close it.'}
                                    className={'escape-menu-lobby'}>
                                {LOBBY_MENU_LABEL}
                            </Button>
                        </Dropdown.Item>
                    }
                    {showPublicToggle &&
                        <Dropdown.Item>
                            {/* CC_LIVING_GAMES_LIST_DESIGN: in-game public
                              * dial. PLACEHOLDER copy — Michael owns the
                              * wording (mirrors the lobby GameConfig
                              * "Public" control). */}
                            <Button style={{ width: '100%' }}
                                    variant={isPublic ? 'honored' : 'stem'}
                                    onClick={handleTogglePublic}
                                    title={'Show or hide this game on the '
                                        + 'public games list.'}
                                    className={'escape-menu-public-toggle'}>
                                {isPublic ? 'Make Private' : 'Make Public'}
                            </Button>
                        </Dropdown.Item>
                    }
                    {showSaveAbandon &&
                        <Dropdown.Item>
                            <Button style={{ width: '100%' }}
                                    variant={'seed'}
                                    onClick={handleSaveExit}
                                    title={'Leave the game. It stays '
                                        + 'saved and resumable.'}
                                    className={'escape-menu-leave-game'}>
                                Leave Game
                            </Button>
                        </Dropdown.Item>
                    }
                    {showSaveAbandon &&
                        <Dropdown.Item>
                            <Button style={{ width: '100%' }}
                                    variant={confirmingAbandon
                                        ? 'dread' : 'outline-dread'}
                                    onClick={handleAbandon}
                                    className={'escape-menu-abandon'}>
                                {confirmingAbandon
                                    ? 'Confirm Abandon'
                                    : 'Abandon'}
                            </Button>
                        </Dropdown.Item>
                    }
                    {pathname && pathname !== '/' &&
                        <Dropdown.Item>
                            <Button style={{ width: '100%' }}
                                    variant={'seed'}
                                    onClick={() => {
                                        props.app.game.returnToMenu(pathname);
                                    }}>Menu</Button>
                        </Dropdown.Item>
                    }
                    {showLogout &&
                        <Dropdown.Item>
                            <Button style={{ width: '100%' }}
                                    variant={'hex'}
                                    onClick={handleLogout}
                                    className={'escape-menu-logout'}>
                                Logout
                            </Button>
                        </Dropdown.Item>
                    }
                    <p style={{textAlign: 'center', fontSize: 'xx-small',
                               margin: '0.25rem 0', lineHeight: '1.1'}}>
                        {BUILD_VERSION}
                        <br/>
                        {BUILD_TIME}
                    </p>
                </Dropdown.Menu>
            </Dropdown></div>
    );
}
